import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Calculate as CalculateIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { GeneratedStory, StoryStatus, TShirtSize, EstimationConfig, EstimationMethodology } from '../types/estimation';
import ForecastCard from '../components/Estimation/ForecastCard';

const JobEstimation: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<any>(null);
  const [stories, setStories] = useState<GeneratedStory[]>([]);
  const [loading, setLoading] = useState(true);
  const [estimationConfigs, setEstimationConfigs] = useState<EstimationConfig[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState<string>('');
  const [selectedMethodology, setSelectedMethodology] = useState<EstimationMethodology | null>(null);
  const [estimationResults, setEstimationResults] = useState<any[]>([]);
  const [estimationLoading, setEstimationLoading] = useState(false);
  const [projectEstimation, setProjectEstimation] = useState<any>(null);
  const [sprints, setSprints] = useState<any[]>([]);
  const [currentSprintId, setCurrentSprintId] = useState<string | null>(null);
  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [dataCollectionComplete, setDataCollectionComplete] = useState(false);
  const [editingStoryData, setEditingStoryData] = useState<{ [storyId: string]: any }>({});

  const steps = ['Select Methodology', 'Collect Data', 'Calculate & Review'];

  useEffect(() => {
    if (jobId) {
      loadJobEstimation();
    }
  }, [jobId]);

  // Handle configId from URL after configs and stories are loaded - auto-skip steps and calculate
  useEffect(() => {
    if (estimationConfigs.length > 0 && stories.length > 0) {
      const urlParams = new URLSearchParams(window.location.search);
      const configIdFromUrl = urlParams.get('configId');
      const autoCalculate = urlParams.get('autoCalculate') === 'true';
      
      if (configIdFromUrl) {
        // If configId is in URL (coming from review page), skip methodology selection
        setSelectedConfigId(configIdFromUrl);
        const config = estimationConfigs.find(c => c.id === configIdFromUrl);
        if (config) {
          setSelectedMethodology(config.methodology);
          
          // Check if data collection is already complete
          const approvedStories = stories.filter(s => s.status === StoryStatus.ACCEPTED);
          let dataComplete = false;
          
          switch (config.methodology) {
            case EstimationMethodology.T_SHIRT_SIZING:
              dataComplete = approvedStories.length > 0 && approvedStories.every(s => 
                s.complexityFactors && s.complexityFactors.frontend && s.complexityFactors.backend
              );
              break;
            case EstimationMethodology.FUNCTION_POINTS:
              dataComplete = approvedStories.length > 0 && approvedStories.every(s => 
                s.functionPointData && (s.functionPointData.externalInputs || s.functionPointData.externalOutputs)
              );
              break;
            case EstimationMethodology.COCOMO:
              dataComplete = approvedStories.length > 0 && approvedStories.every(s => 
                s.estimatedLinesOfCode && s.estimatedLinesOfCode > 0
              );
              break;
          }
          
          // If data is complete and autoCalculate is true, go directly to calculation
          if (dataComplete && autoCalculate) {
            setActiveStep(2); // Skip directly to calculation
            setDataCollectionComplete(true);
            // Auto-calculate immediately (will be handled by Step 2 rendering)
          } else {
            setActiveStep(1); // Go to data collection
          }
        }
      } else if (!selectedConfigId && estimationConfigs.length > 0) {
        // Default to first config if no URL param
        setSelectedConfigId(estimationConfigs[0].id);
        setSelectedMethodology(estimationConfigs[0].methodology);
      }
    }
  }, [estimationConfigs, stories]);

  const loadJobEstimation = async () => {
    try {
      setLoading(true);
      
      // Load job and stories
      const data = await apiService.getStoryGenerationJob(jobId!);
      setJob(data.job);
      setStories(data.stories);
      
      // Load estimation configs
      const configs = await apiService.getEstimationConfigs();
      setEstimationConfigs(configs);
      if (configs.length > 0 && !selectedConfigId) {
        setSelectedConfigId(configs[0].id);
        setSelectedMethodology(configs[0].methodology);
      }
      
      // Load templates
      try {
        const templatesData = await apiService.getEstimationTemplates();
        setTemplates(templatesData.data || templatesData || []);
      } catch (error) {
        console.error('Error loading templates:', error);
      }
      
      // Load existing estimation results
      try {
        const results = await apiService.getEstimationResults(jobId!);
        setEstimationResults(results);
        if (results.length > 0) {
          setActiveStep(2); // Already calculated
          setDataCollectionComplete(true);
        }
      } catch (error) {
        setEstimationResults([]);
      }
      
      // Load sprints for the project if available
      if (data.job?.project?.id) {
        try {
          const sprintsData = await apiService.getProjectSprints(data.job.project.id, false);
          setSprints(sprintsData);
          if (sprintsData.length > 0) {
            setCurrentSprintId(sprintsData[0].id);
          }
        } catch (error) {
          console.error('Error loading sprints:', error);
        }
      }
    } catch (error) {
      console.error('Error loading job estimation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMethodologyChange = (configId: string) => {
    setSelectedConfigId(configId);
    const config = estimationConfigs.find(c => c.id === configId);
    if (config) {
      setSelectedMethodology(config.methodology);
      setActiveStep(1); // Move to data collection step
      setDataCollectionComplete(false);
    }
  };

  const handleUpdateStoryData = async (storyId: string, data: any) => {
    try {
      await apiService.updateGeneratedStory(storyId, data);
      setStories(prev => prev.map(s => s.id === storyId ? { ...s, ...data } : s));
      setEditingStoryData(prev => ({ ...prev, [storyId]: null }));
      await loadJobEstimation();
    } catch (error) {
      console.error('Error updating story data:', error);
      alert('Failed to update story data');
    }
  };

  const checkDataCollectionComplete = () => {
    const approvedStories = stories.filter(s => s.status === StoryStatus.ACCEPTED);
    if (approvedStories.length === 0) return false;

    switch (selectedMethodology) {
      case EstimationMethodology.T_SHIRT_SIZING:
        // T-Shirt sizing: check if complexity factors exist
        return approvedStories.every(s => s.complexityFactors && 
          s.complexityFactors.frontend && s.complexityFactors.backend);
      
      case EstimationMethodology.FUNCTION_POINTS:
        // Function Points: check if FP data exists for all stories
        return approvedStories.every(s => s.functionPointData &&
          (s.functionPointData.externalInputs || s.functionPointData.externalOutputs ||
           s.functionPointData.externalInquiries || s.functionPointData.internalLogicalFiles ||
           s.functionPointData.externalInterfaceFiles));
      
      case EstimationMethodology.COCOMO:
        // COCOMO: check if KLOC exists for all stories
        return approvedStories.every(s => s.estimatedLinesOfCode && s.estimatedLinesOfCode > 0);
      
      default:
        return false;
    }
  };

  const handleCalculateEstimation = async () => {
    if (!selectedConfigId) {
      alert('Please select an estimation configuration');
      return;
    }

    const approvedStories = stories.filter(story => story.status === StoryStatus.ACCEPTED);
    if (approvedStories.length === 0) {
      alert('No approved stories found for estimation.');
      return;
    }

    if (!checkDataCollectionComplete()) {
      alert('Please complete data collection for all approved stories before calculating.');
      return;
    }

    try {
      setEstimationLoading(true);
      const result = await apiService.calculateJobEstimation(jobId!, selectedConfigId);
      
      const results = await apiService.getEstimationResults(jobId!);
      setEstimationResults(results);
      setProjectEstimation(result.projectEstimation);
      setActiveStep(2);
      setDataCollectionComplete(true);
      
      alert(`Estimation completed! Total effort: ${result.projectEstimation.totalEffort.toFixed(2)} person days`);
    } catch (error) {
      console.error('Error calculating estimation:', error);
      alert('Error calculating estimation. Please try again.');
    } finally {
      setEstimationLoading(false);
    }
  };

  const getEstimationForStory = (storyId: string) => {
    return estimationResults.find(result => result.storyId === storyId);
  };

  const getTShirtSizeColor = (size: TShirtSize) => {
    switch (size) {
      case TShirtSize.XS: return 'default';
      case TShirtSize.S: return 'primary';
      case TShirtSize.M: return 'secondary';
      case TShirtSize.L: return 'warning';
      case TShirtSize.XL: return 'error';
      case TShirtSize.XXL: return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const renderFunctionPointInput = (story: GeneratedStory) => {
    const fpData = story.functionPointData || {
      externalInputs: { simple: 0, average: 0, complex: 0 },
      externalOutputs: { simple: 0, average: 0, complex: 0 },
      externalInquiries: { simple: 0, average: 0, complex: 0 },
      internalLogicalFiles: { simple: 0, average: 0, complex: 0 },
      externalInterfaceFiles: { simple: 0, average: 0, complex: 0 }
    };

    const updateFPData = (type: string, complexity: string, value: number) => {
      const newData = {
        ...fpData,
        [type]: {
          ...fpData[type as keyof typeof fpData],
          [complexity]: Math.max(0, value)
        }
      };
      handleUpdateStoryData(story.id, { functionPointData: newData });
    };

    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
          Function Point Data for: {story.title}
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell align="center">Simple</TableCell>
              <TableCell align="center">Average</TableCell>
              <TableCell align="center">Complex</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>External Inputs (EI)</TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={fpData.externalInputs.simple}
                  onChange={(e) => updateFPData('externalInputs', 'simple', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                  sx={{ width: 80 }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={fpData.externalInputs.average}
                  onChange={(e) => updateFPData('externalInputs', 'average', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                  sx={{ width: 80 }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={fpData.externalInputs.complex}
                  onChange={(e) => updateFPData('externalInputs', 'complex', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                  sx={{ width: 80 }}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>External Outputs (EO)</TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={fpData.externalOutputs.simple}
                  onChange={(e) => updateFPData('externalOutputs', 'simple', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                  sx={{ width: 80 }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={fpData.externalOutputs.average}
                  onChange={(e) => updateFPData('externalOutputs', 'average', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                  sx={{ width: 80 }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={fpData.externalOutputs.complex}
                  onChange={(e) => updateFPData('externalOutputs', 'complex', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                  sx={{ width: 80 }}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>External Inquiries (EQ)</TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={fpData.externalInquiries.simple}
                  onChange={(e) => updateFPData('externalInquiries', 'simple', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                  sx={{ width: 80 }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={fpData.externalInquiries.average}
                  onChange={(e) => updateFPData('externalInquiries', 'average', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                  sx={{ width: 80 }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={fpData.externalInquiries.complex}
                  onChange={(e) => updateFPData('externalInquiries', 'complex', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                  sx={{ width: 80 }}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Internal Logical Files (ILF)</TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={fpData.internalLogicalFiles.simple}
                  onChange={(e) => updateFPData('internalLogicalFiles', 'simple', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                  sx={{ width: 80 }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={fpData.internalLogicalFiles.average}
                  onChange={(e) => updateFPData('internalLogicalFiles', 'average', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                  sx={{ width: 80 }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={fpData.internalLogicalFiles.complex}
                  onChange={(e) => updateFPData('internalLogicalFiles', 'complex', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                  sx={{ width: 80 }}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>External Interface Files (EIF)</TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={fpData.externalInterfaceFiles.simple}
                  onChange={(e) => updateFPData('externalInterfaceFiles', 'simple', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                  sx={{ width: 80 }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={fpData.externalInterfaceFiles.average}
                  onChange={(e) => updateFPData('externalInterfaceFiles', 'average', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                  sx={{ width: 80 }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={fpData.externalInterfaceFiles.complex}
                  onChange={(e) => updateFPData('externalInterfaceFiles', 'complex', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                  sx={{ width: 80 }}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    );
  };

  const renderCOCOMOInput = (story: GeneratedStory) => {
    const kloc = story.estimatedLinesOfCode || 0;

    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
          Lines of Code Estimate for: {story.title}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              label="Estimated Lines of Code"
              type="number"
              fullWidth
              value={kloc}
              onChange={(e) => {
                const value = parseFloat(e.target.value) || 0;
                handleUpdateStoryData(story.id, { estimatedLinesOfCode: value });
              }}
              helperText="Enter estimated LOC (will be converted to KLOC automatically)"
              inputProps={{ min: 0, step: 100 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Alert severity="info">
              <Typography variant="body2">
                <strong>KLOC:</strong> {(kloc / 1000).toFixed(2)} thousand lines
                <br />
                <small>Typical estimates: Simple (500-1000), Medium (1000-3000), Complex (3000-5000)</small>
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderDataCollection = () => {
    const approvedStories = stories.filter(s => s.status === StoryStatus.ACCEPTED);
    
    if (approvedStories.length === 0) {
      return (
        <Alert severity="warning">
          No approved stories found. Please review and approve stories first.
        </Alert>
      );
    }

    switch (selectedMethodology) {
      case EstimationMethodology.FUNCTION_POINTS:
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Function Point Analysis:</strong> Please provide counts for each type of function point component.
                For each story, enter the number of Simple, Average, and Complex items.
              </Typography>
            </Alert>
            {approvedStories.map((story, index) => (
              <Accordion key={story.id} defaultExpanded={index === 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {index + 1}. {story.title}
                    </Typography>
                    {story.functionPointData && (
                      <Chip label="Data Provided" color="success" size="small" />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {renderFunctionPointInput(story)}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        );

      case EstimationMethodology.COCOMO:
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>COCOMO Model:</strong> Please provide estimated Lines of Code (LOC) for each story.
                The system will convert to KLOC and calculate effort using COCOMO formulas.
              </Typography>
            </Alert>
            {approvedStories.map((story, index) => (
              <Card key={story.id} sx={{ mb: 2 }}>
                {renderCOCOMOInput(story)}
              </Card>
            ))}
          </Box>
        );

      case EstimationMethodology.T_SHIRT_SIZING:
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>T-Shirt Sizing:</strong> Complexity factors are already collected during story review.
                If needed, you can update them below. The system will use these to calculate effort.
              </Typography>
            </Alert>
            <Alert severity="success" sx={{ mb: 2 }}>
              ✓ T-Shirt sizing data is ready. You can proceed to calculation.
            </Alert>
          </Box>
        );

      default:
        return (
          <Alert severity="warning">
            Please select an estimation methodology first.
          </Alert>
        );
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!job) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Job not found</Alert>
      </Box>
    );
  }

  const approvedStories = stories.filter(story => story.status === StoryStatus.ACCEPTED);
  const totalEffort = estimationResults.reduce((sum, result) => sum + result.totalEffort, 0);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate(`/estimation/job/${jobId}`)}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Estimation: {job.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={job.status}
              color={job.status === 'COMPLETED' ? 'success' : job.status === 'FAILED' ? 'error' : 'default'}
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              Created: {formatDate(job.createdAt)}
            </Typography>
            {job.project && (
              <Chip label={job.project.name} size="small" variant="outlined" />
            )}
          </Box>
        </Box>
        <IconButton onClick={loadJobEstimation} title="Refresh">
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Workflow Stepper */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 0: Methodology Selection - Only show if no configId in URL */}
                 {activeStep === 0 && !new URLSearchParams(window.location.search).get('configId') && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Step 1: Select Estimation Methodology
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Choose an estimation methodology. Each methodology requires different types of data,
                  which you'll collect in the next step.
                </Typography>
              </Alert>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <FormControl fullWidth>
                    <InputLabel>Estimation Configuration</InputLabel>
                    <Select
                      value={selectedConfigId}
                      onChange={(e) => handleMethodologyChange(e.target.value)}
                      label="Estimation Configuration"
                    >
                      {estimationConfigs.map((config) => (
                        <MenuItem key={config.id} value={config.id}>
                          <Box>
                            <Typography variant="body2">{config.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {config.methodology.replace('_', ' ')} • {config.teamVelocity} velocity
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    variant="outlined"
                    onClick={() => setShowTemplateDialog(true)}
                    fullWidth
                  >
                    Apply Template
                  </Button>
                </Grid>
              </Grid>

              {selectedConfigId && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="success">
                    <Typography variant="body2">
                      <strong>Selected:</strong> {estimationConfigs.find(c => c.id === selectedConfigId)?.name}
                      <br />
                      <strong>Methodology:</strong> {selectedMethodology?.replace('_', ' ')}
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => setActiveStep(1)}
                      sx={{ mt: 1 }}
                    >
                      Proceed to Data Collection
                    </Button>
                  </Alert>
                </Box>
              )}
            </Box>
          )}

          {/* Step 1: Data Collection */}
          {activeStep === 1 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {selectedMethodology ? `Collect ${selectedMethodology.replace('_', ' ')} Data` : 'Collect Methodology-Specific Data'}
                </Typography>
                {!new URLSearchParams(window.location.search).get('configId') && (
                  <Button
                    variant="outlined"
                    onClick={() => setActiveStep(0)}
                    size="small"
                  >
                    Change Methodology
                  </Button>
                )}
              </Box>

              {selectedMethodology && renderDataCollection()}

              <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                {!new URLSearchParams(window.location.search).get('configId') && (
                  <Button onClick={() => setActiveStep(0)}>Back</Button>
                )}
                <Button
                  variant="contained"
                  onClick={async () => {
                    if (checkDataCollectionComplete()) {
                      setActiveStep(2);
                      setDataCollectionComplete(true);
                      // Auto-calculate if coming from review page
                      if (new URLSearchParams(window.location.search).get('autoCalculate') === 'true') {
                        await handleCalculateEstimation();
                      }
                    } else {
                      alert('Please complete data collection for all approved stories.');
                    }
                  }}
                  disabled={!checkDataCollectionComplete()}
                >
                  {new URLSearchParams(window.location.search).get('autoCalculate') === 'true'
                    ? 'Save & Calculate Estimation'
                    : checkDataCollectionComplete() ? 'Proceed to Calculation' : 'Complete Data Collection First'
                  }
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 2: Calculation */}
          {activeStep === 2 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Step 3: Calculate & Review Results
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {!dataCollectionComplete && (
                    <Button
                      variant="outlined"
                      onClick={() => setActiveStep(1)}
                      size="small"
                    >
                      Edit Data
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    onClick={handleCalculateEstimation}
                    disabled={estimationLoading || !selectedConfigId}
                    startIcon={estimationLoading ? <CircularProgress size={16} /> : <CalculateIcon />}
                  >
                    {estimationLoading ? 'Calculating...' : 'Calculate Estimation'}
                  </Button>
                </Box>
              </Box>

              {estimationResults.length === 0 && (
                <Alert severity="info">
                  Click "Calculate Estimation" to generate estimates based on your collected data.
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Project Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">Total Stories</Typography>
              <Typography variant="h6">{stories.length}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">Approved Stories</Typography>
              <Typography variant="h6" color="success.main">{approvedStories.length}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">Total Effort</Typography>
              <Typography variant="h6" color="primary.main">
                {totalEffort.toFixed(2)} days
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">Average per Story</Typography>
              <Typography variant="h6" color="secondary.main">
                {approvedStories.length > 0 ? (totalEffort / approvedStories.length).toFixed(2) : '0'} days
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Template Dialog */}
      <Dialog open={showTemplateDialog} onClose={() => setShowTemplateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Apply Estimation Template</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Template</InputLabel>
              <Select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                label="Select Template"
              >
                <MenuItem value="">None</MenuItem>
                {templates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    <Box>
                      <Typography variant="body2">{template.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {template.description || template.templateType}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTemplateDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (selectedTemplateId && selectedConfigId) {
                try {
                  const config = estimationConfigs.find(c => c.id === selectedConfigId);
                  if (config) {
                    const response = await apiService.applyTemplate(selectedTemplateId, config);
                    if (response?.success) {
                      await loadJobEstimation();
                      setShowTemplateDialog(false);
                      alert('Template applied successfully!');
                    } else {
                      alert(response?.message || 'Failed to apply template');
                    }
                  }
                } catch (error) {
                  console.error('Error applying template:', error);
                  alert('Failed to apply template');
                }
              }
            }}
            disabled={!selectedTemplateId || !selectedConfigId}
          >
            Apply Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Results Table - Only show if calculated */}
      {estimationResults.length > 0 && (
        <Paper sx={{ width: '100%', overflow: 'hidden', mb: 3 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">
              Estimation Results ({estimationResults.length} stories)
            </Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ width: 60 }}>#</TableCell>
                  <TableCell>Story Title</TableCell>
                  <TableCell>Description</TableCell>
                  {selectedMethodology === EstimationMethodology.T_SHIRT_SIZING && (
                    <TableCell>Size</TableCell>
                  )}
                  {selectedMethodology === EstimationMethodology.COCOMO && (
                    <TableCell>LOC</TableCell>
                  )}
                  {selectedMethodology === EstimationMethodology.FUNCTION_POINTS && (
                    <TableCell>FP Type</TableCell>
                  )}
                  {/* Methodology-specific columns */}
                  {selectedMethodology === EstimationMethodology.T_SHIRT_SIZING && (
                    <>
                      <TableCell align="center">Base Points</TableCell>
                      <TableCell align="center">Adjusted Points</TableCell>
                    </>
                  )}
                  {selectedMethodology === EstimationMethodology.FUNCTION_POINTS && (
                    <>
                      <TableCell align="center">Unadjusted FP</TableCell>
                      <TableCell align="center">Adjusted FP</TableCell>
                    </>
                  )}
                  {selectedMethodology === EstimationMethodology.COCOMO && (
                    <>
                      <TableCell align="center">Effort (PM)</TableCell>
                      <TableCell align="center">Duration (Months)</TableCell>
                    </>
                  )}
                  <TableCell align="center">Person Days</TableCell>
                  <TableCell align="center">Total Effort</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {approvedStories.map((story, index) => {
                  const estimation = getEstimationForStory(story.id);
                  return (
                    <TableRow key={story.id} hover>
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {story.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {truncateText(story.description || '', 80)}
                        </Typography>
                      </TableCell>
                      {/* Methodology-specific Size/LOC/FP display */}
                      {selectedMethodology === EstimationMethodology.T_SHIRT_SIZING && (
                        <TableCell>
                          <Chip
                            label={story.estimatedSize || 'M'}
                            color={getTShirtSizeColor(story.estimatedSize || TShirtSize.M) as any}
                            size="small"
                          />
                        </TableCell>
                      )}
                      {selectedMethodology === EstimationMethodology.COCOMO && (
                        <TableCell>
                          {story.estimatedLinesOfCode ? (
                            <Typography variant="body2">
                              {story.estimatedLinesOfCode.toLocaleString()} LOC
                              <br />
                              <small>({(story.estimatedLinesOfCode / 1000).toFixed(2)} KLOC)</small>
                            </Typography>
                          ) : '-'}
                        </TableCell>
                      )}
                      {selectedMethodology === EstimationMethodology.FUNCTION_POINTS && (
                        <TableCell>
                          {story.functionPointData ? (
                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                              FP Data
                            </Typography>
                          ) : '-'}
                        </TableCell>
                      )}
                      
                      {/* Methodology-specific metric columns */}
                      {selectedMethodology === EstimationMethodology.T_SHIRT_SIZING && (
                        <>
                          <TableCell align="center">
                            {estimation ? estimation.baseStoryPoints.toFixed(1) : '-'}
                          </TableCell>
                          <TableCell align="center">
                            {estimation ? (
                              <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                                {estimation.adjustedStoryPoints.toFixed(1)}
                              </Typography>
                            ) : '-'}
                          </TableCell>
                        </>
                      )}
                      {selectedMethodology === EstimationMethodology.FUNCTION_POINTS && (
                        <>
                          <TableCell align="center">
                            {estimation ? (estimation.baseStoryPoints / 0.1).toFixed(1) : '-'}
                          </TableCell>
                          <TableCell align="center">
                            {estimation ? (
                              <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                                {(estimation.adjustedStoryPoints / 0.1).toFixed(1)}
                              </Typography>
                            ) : '-'}
                          </TableCell>
                        </>
                      )}
                      {selectedMethodology === EstimationMethodology.COCOMO && (() => {
                        if (!estimation) return (
                          <>
                            <TableCell align="center">-</TableCell>
                            <TableCell align="center">-</TableCell>
                          </>
                        );
                        
                        // Calculate COCOMO metrics from personDays
                        // personDays = detailedEffort * 22, so detailedEffort = personDays / 22
                        const detailedEffort = estimation.personDays / 22;
                        const selectedConfig = estimationConfigs.find(c => c.id === selectedConfigId);
                        const projectType = selectedConfig?.cocomoModel?.projectType || 'organic';
                        
                        // COCOMO duration formula: duration = c * (effort ^ d)
                        const coefficients = {
                          organic: { c: 2.5, d: 0.38 },
                          'semi-detached': { c: 2.5, d: 0.35 },
                          embedded: { c: 2.5, d: 0.32 }
                        };
                        const coef = coefficients[projectType];
                        const duration = coef.c * Math.pow(detailedEffort, coef.d);
                        
                        return (
                          <>
                            <TableCell align="center">
                              {detailedEffort.toFixed(2)} PM
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                                {duration.toFixed(2)} months
                              </Typography>
                            </TableCell>
                          </>
                        );
                      })()}
                      
                      <TableCell align="center">
                        {estimation ? estimation.personDays.toFixed(1) : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {estimation ? (
                          <Chip
                            label={`${estimation.totalEffort.toFixed(1)} days`}
                            color="success"
                            size="small"
                            variant="outlined"
                          />
                        ) : '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* AI/ML Forecasts */}
      {estimationResults.length > 0 && approvedStories.length > 0 && (
        <Grid container spacing={3}>
          {job?.project?.id && (
            <Grid item xs={12} md={6}>
              <ForecastCard type="project" id={job.project.id} />
            </Grid>
          )}
          {currentSprintId && (
            <Grid item xs={12} md={6}>
              <ForecastCard type="sprint" id={currentSprintId} />
            </Grid>
          )}
          {totalEffort > 0 && estimationResults.length > 0 && (
            <Grid item xs={12}>
              <ForecastCard
                type="effort"
                id="total-effort"
                storyPoints={(() => {
                  const calculated = Math.round(totalEffort * (estimationConfigs.find(c => c.id === selectedConfigId)?.teamVelocity || 1));
                  // Ensure it's a valid number between 1 and 1000
                  return Math.max(1, Math.min(1000, calculated || 1));
                })()}
                projectId={job?.project?.id}
              />
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default JobEstimation;
